import React, { useEffect } from "react";
import {
  ForeignKeyComponent,
  ForeignKeyFieldOptions,
  ForeignKeyRenderer,
  ListDataSource,
  LoggerFactory,
  TypeComponentType,
  Validator
} from "lego-gui-lib";
import { Required, Type } from "lego-gui-lib/lib";
import { CustomForeignKeyRenderer } from "./CustomForeignKeyRenderer";
import { CustomForeignKeyComponent } from "./CustomForeignKeyComponent";

const log = LoggerFactory.create("ForeignKeyField");

export interface ForeignKeyFieldWithPageSizeOptions
  extends ForeignKeyFieldOptions {
  dataSource: ListDataSource<{}, {}>;
  valueField: string;
  labelField: string | ((option: {}) => string);
  searchField?: string;
  /** Optional placeholder text */
  placeholder?: string;
  renderSelectedOptions?: (option: {}) => string;
  /** path for link to foreign key source view */
  routeDestination?: string;
  /** optional parameter name, added after route destination path ( default 'valueField' ) */
  routeDestinationField?: string;
  filter?: string;
  isMulti?: boolean;
  minWidth?: number;
  /** optional number of fetched items, defaults to 1000 */
  pageSize?: number;
}

export const ForeignKeyFieldWithPageSize = (
  options: ForeignKeyFieldWithPageSizeOptions,
  ...validators: Validator[]
) => {
  const hasRequiredValidator = validators.indexOf(Required) !== -1;

  return new Type({
    Component: DsReloader(
      options,
      CustomForeignKeyComponent
    ) as TypeComponentType,
    PresentationComponent: DsReloader(
      options,
      CustomForeignKeyRenderer
    ) as TypeComponentType,
    props: {
      clearable: !hasRequiredValidator,
      ...options
    },
    validators
  });
};

const noNeedToUpdateDataSources: ListDataSource<any, any>[] = [];
const DsReloader = (
  options: ForeignKeyFieldWithPageSizeOptions,
  Component: any
) => {
  // @ts-ignore
  options.dataSource.onEvent &&
    // @ts-ignore
    options.dataSource.onEvent("parentChanged", () => {
      log.debug(
        "parent change so I am removing ds from noNeedToUpdateDataSources list",
        options.dataSource
      );
      const idx = noNeedToUpdateDataSources.indexOf(options.dataSource);
      if (idx !== -1) {
        noNeedToUpdateDataSources.splice(idx, 1);
      }
    });

  return function WrapperComponent(props: any) {
    useEffect(() => {
      if (noNeedToUpdateDataSources.indexOf(options.dataSource) === -1) {
        log.debug(
          "dataSource is not on a noNeedToUpdateDataSources list so I am reloading it"
        );
        fetchSelectOptions(options);
        noNeedToUpdateDataSources.push(options.dataSource);
      } else {
        log.debug(
          "dataSource is on a noNeedToUpdateDataSources list so I am NOT reloading it"
        );
      }
    }, []);

    return <Component {...props} />;
  };
};

const fetchSelectOptions = (options: ForeignKeyFieldWithPageSizeOptions) => {
  let sortField: string;
  if (options.labelField instanceof Function) {
    if (!options.searchField) {
      throw new Error("searchField is mandatory if labelField is a function");
    }
    sortField = options.searchField;
  } else {
    sortField = options.labelField;
  }

  return options.dataSource.findAll({
    filters: [],
    page: { pageSize: options.pageSize || 1000, pageNumber: 0 },
    sort: {
      order: "asc",
      sort: sortField
    }
  });
};

ForeignKeyFieldWithPageSize.noNeedToUpdateDataSources = noNeedToUpdateDataSources;
